// Where the open file lives in Drive — "My Drive › Projects › Q3", or
// "Shared with me › …" for someone else's file. Each segment opens that
// folder in Drive (new tab, so the document stays open). Identical file in
// Docs, Sheets and Slides.

import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { ChevronRight, HardDrive, Users } from "@fg/icons";
import { cx } from "@/features/home/HomeUi";
import { driveFileApi, driveLinks, type FolderRef } from "./driveFileApi";
import type { HeaderTone } from "./ShareDialog";

// Deep paths keep the first folder and the last two: "My Drive › A › … › Y › Z".
const MAX_FOLDERS = 3;

interface Segment {
    key: string;
    label: string;
    href?: string;
    /** Full path of a collapsed run, for its tooltip. */
    title?: string;
}

export function DriveLocation({
    fileId, parentId, shared, tone, className
}: {
    fileId: string;
    /** Refetches when the file moves. */
    parentId: string | null;
    /** Someone else's file (the caller has a viewer / editor role on it). */
    shared: boolean;
    tone: HeaderTone;
    className?: string;
}) {
    const auth = useAuth();
    const tokenRef = useRef(auth.user?.access_token);
    tokenRef.current = auth.user?.access_token;
    const [folders, setFolders] = useState<FolderRef[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        setFolders(null);
        driveFileApi.ancestors(fileId, tokenRef.current).then(
            (chain) => { if (!cancelled) setFolders(chain.filter((f) => f.id !== fileId)); },
            // Path unknown (e.g. access just changed) — show the root alone.
            () => { if (!cancelled) setFolders([]); }
        );
        return () => { cancelled = true; };
    }, [fileId, parentId]);

    const RootIcon = shared ? Users : HardDrive;
    const segments: Segment[] = [
        shared
            ? { key: "root", label: "Shared with me", href: driveLinks.sharedWithMe() }
            : { key: "root", label: "My Drive", href: driveLinks.folder(null) }
    ];
    const chain = folders ?? [];
    const visible = chain.length > MAX_FOLDERS
        ? [chain[0], null, ...chain.slice(-(MAX_FOLDERS - 1))]
        : chain;
    for (const f of visible) {
        if (f === undefined) continue;
        if (f === null) {
            const hidden = chain.slice(1, chain.length - (MAX_FOLDERS - 1)).map((h) => h.name).join(" › ");
            segments.push({ key: "more", label: "…", title: hidden });
        } else {
            segments.push({ key: f.id, label: f.name, href: driveLinks.folder(f.id) });
        }
    }

    // "onPrimary" = on the suite top bar (Accent, Light or Dark): inherit its colour.
    const link = tone === "onPrimary"
        ? "opacity-80 hover:opacity-100 focus-visible:ring-current"
        : "text-muted-foreground hover:text-foreground focus-visible:ring-ring";

    return (
        <nav aria-label="Location in Drive" className={cx("min-w-0 items-center text-xs leading-4", className ?? "flex")}>
            <ol className="flex min-w-0 items-center gap-0.5">
                {segments.map((s, i) => (
                    <li key={s.key} className={cx("flex items-center gap-0.5", i === 0 ? "shrink-0" : "min-w-0")}>
                        {i > 0 ? (
                            <ChevronRight
                                className={cx("h-3 w-3 shrink-0", tone === "onPrimary" ? "opacity-60" : "text-muted-foreground/70")}
                                aria-hidden
                            />
                        ) : null}
                        {s.href ? (
                            <a
                                href={s.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Open “${s.label}” in Drive`}
                                aria-current={i === segments.length - 1 ? "location" : undefined}
                                className={cx(
                                    "flex min-w-0 items-center gap-1 rounded px-0.5 hover:underline focus-visible:outline-none focus-visible:ring-2",
                                    link
                                )}
                            >
                                {i === 0 ? <RootIcon className="h-3 w-3 shrink-0" aria-hidden /> : null}
                                <span className="max-w-[10rem] truncate">{s.label}</span>
                            </a>
                        ) : (
                            <span title={s.title} className={cx("px-0.5", tone === "onPrimary" ? "opacity-70" : "text-muted-foreground")}>
                                {s.label}
                            </span>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    );
}
